<template>
  <FriedModal @close="$emit('close')">
    <template #header>
      <FriedModalHeader :header="modalTitle" />
    </template>
    <div class="participant-list-container">
      <FriedList class="participant-list">
        <FriedListItem
          v-for="participant of participants"
          :key="participant.id"
          :title="formatTitle(participant) || '-'"
          :description="formatDescription(participant)"
          :show-description="!!formatDescription(participant)"
          :class="{ removed: !participant.id || participant.deletedAt }"
          :data-external="`chat-participant-list-item:${participant.email}`"
        >
          <template #left>
            <div class="participant-list-icon-container">
              <Avatar
                :name="formatName(participant)"
                :thumb-url="participant.thumbUrl"
                :deleted-at="participant.deletedAt"
              />
            </div>
          </template>
        </FriedListItem>
      </FriedList>
    </div>
  </FriedModal>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Participant } from '../types';
import AvatarHelper from '../helpers/avatar.helper';
import ParticipantHelper from '../helpers/participant.helper';
import Avatar from './Avatar.vue';

export default defineComponent({
  components: {
    Avatar,
  },
  props: {
    participants: {
      type: Array as PropType<Partial<Participant>[]>,
      default: () => [],
    },
    modalTitle: {
      type: String,
      default: '',
    },
    showTitle: {
      type: Boolean,
    },
    showCurrentUserLabel: {
      type: Boolean,
    },
    currentUserId: {
      type: String,
      default: undefined,
    },
  },
  emits: ['close'],
  setup(props) {
    const { t } = useI18n();
    const formatName = (participant: Partial<Participant>) =>
      AvatarHelper.getDisplayName(participant);
    const formatDescription = (participant: Partial<Participant>) =>
      participant.email || participant.phone;

    const titleText = (participant: Partial<Participant>) =>
      ParticipantHelper.formatTitleAndCompanyName(participant);

    const showYouLabel = (participant: Partial<Participant>) =>
      [participant.id, participant.newParticipantId].includes(props.currentUserId)
        ? ` (${t('you')})`
        : '';

    const formatTitle = (participant: Partial<Participant>) =>
      props.showTitle
        ? props.showCurrentUserLabel
          ? `${formatName(participant)}${titleText(participant)}${showYouLabel(participant)}`
          : `${formatName(participant)}${titleText(participant)}`
        : `${formatName(participant)}${showYouLabel(participant)}`;

    return { formatTitle, formatName, formatDescription };
  },
});
</script>

<style lang="scss" scoped>
@import '../scss/removed-participant';

.participant-list {
  :deep(.list-item) {
    .list-item-main-content {
      width: 100%;
    }

    .list-information {
      /* stylelint-disable-next-line value-no-vendor-prefix */
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
}
</style>
